import { LegalPage } from "../../components/LegalPage";

export function SmsTerms() {
  return (
    <LegalPage
      title="SMS & RCS Terms & Opt-In Disclosure"
      description="WeConsult Rx text messaging (SMS and RCS) program terms: opt-in, message frequency, opt-out, message and data rates, and Twilio A2P 10DLC + TCPA compliance disclosures."
      path="/sms-terms"
      effective="2026-08-05"
    >
      <p>
        This page describes the WeConsult Rx text-message program, which
        includes both SMS and RCS (Rich Communication Services) messages,
        including how you opt in, what messages you can expect, how to opt out,
        and required Twilio A2P 10DLC and US TCPA (Telephone Consumer Protection
        Act) disclosures. RCS is a richer text-message format delivered over the
        same program and consent as SMS — the same opt-in, opt-out, frequency,
        and non-sharing terms below apply to both. By providing your mobile
        phone number and opting in, you agree to these Terms for both SMS and
        RCS messages.
      </p>

      <h2>Program description</h2>
      <p>
        <strong>Program name:</strong> WeConsult Rx Notifications
        <br />
        <strong>Message types:</strong> SMS and RCS (Rich Communication
        Services).
        <br />
        <strong>Brand:</strong> WeConsult Rx, operated by DRX Consulting Group,
        LLC.
        <br />
        <strong>Use cases:</strong> account, transactional, and operational
        messages — including (a) appointment / review reminders for consultant
        pharmacist workflows, (b) license renewal and lab-due reminders for
        facility staff, (c) report bundle signing confirmations, (d) alerts to
        remove medications in facility stock at or past their beyond-use date,
        and (e) FDA drug shortage and recall notices affecting the facility’s
        formulary. Categories (d) and (e) may name the medication, its strength,
        storage location and date, and may carry a link into your own
        authenticated account. They describe facility inventory only — never a
        patient, resident, or their care.
      </p>
      <p>
        We do <strong>not</strong> use this SMS or RCS program for promotional
        or marketing messages. Marketing communications, if any, are sent only
        by email and only with separate, explicit consent.
      </p>

      <h2>How to opt in</h2>
      <p>You can opt in to the WeConsult Rx SMS program in any of the following ways:</p>
      <ul>
        <li>
          <strong>Public opt-in form:</strong> visit{" "}
          <a href="/sms-opt-in">weconsultrx.com/sms-opt-in</a>, enter your
          mobile phone number, and check the consent box agreeing to receive
          SMS messages from WeConsult Rx for the purposes described in these
          SMS Terms. No account is required. The demo-request and contact forms
          at <a href="https://weconsultrx.com">weconsultrx.com</a> also carry
          the same optional SMS-consent checkbox.
        </li>
        <li>
          <strong>Text reply:</strong> by replying <code>START</code> or{" "}
          <code>YES</code> to any prior WeConsult Rx SMS message after opting
          out.
        </li>
      </ul>
      <p>
        Each opt-in is logged with a timestamp, the channel of consent, the IP
        address (where applicable), and the user identifier. We retain these
        records for the duration of the program plus seven (7) years to comply
        with TCPA recordkeeping expectations.
      </p>

      <h2>Sample messages</h2>
      <p>You may receive messages similar to the following:</p>
      <ul>
        <li>
          <em>
            “WeConsult Rx: Your monthly review bundle for Sunset Manor is ready
            for signature. View: https://app.weconsultrx.com/bundles/&lt;id&gt;
            Reply STOP to opt out.”
          </em>
        </li>
        <li>
          <em>
            “WeConsult Rx: Reminder — your state pharmacist license renewal
            expires in 30 days. Reply STOP to opt out.”
          </em>
        </li>
      </ul>

      <h2>Message frequency</h2>
      <p>
        Message frequency varies based on your facility’s activity and the
        notification kinds you enable. A typical user receives between 0 and 8
        messages per week.
      </p>

      <h2>Message and data rates</h2>
      <p>
        <strong>Message and data rates may apply.</strong> WeConsult Rx does
        not charge for SMS messages, but your mobile carrier may charge you for
        messages sent to or from your device. Contact your carrier for details
        on your plan.
      </p>

      <h2>How to opt out</h2>
      <p>
        You can opt out of the WeConsult Rx SMS program at any time by:
      </p>
      <ul>
        <li>
          <strong>Reply STOP to cancel.</strong> Replying <code>STOP</code>,{" "}
          <code>STOPALL</code>, <code>END</code>, <code>QUIT</code>,{" "}
          <code>CANCEL</code>, or <code>UNSUBSCRIBE</code> to any WeConsult Rx
          SMS message will unsubscribe you. You will receive a single
          confirmation that you have been unsubscribed, and you will not
          receive further SMS messages from the program until you opt back in.
        </li>
        <li>
          Toggling the SMS channel off in the WeConsult Rx application under
          Settings → Notifications.
        </li>
        <li>
          Emailing{" "}
          <a href="mailto:sms-optout@weconsultrx.com">
            sms-optout@weconsultrx.com
          </a>{" "}
          with the phone number(s) you wish to remove.
        </li>
      </ul>

      <h2>How to get help</h2>
      <p>
        Reply <code>HELP</code> to any WeConsult Rx SMS message to receive
        these contact details. You can also email{" "}
        <a href="mailto:support@weconsultrx.com">support@weconsultrx.com</a> or
        visit <a href="/contact">our contact page</a>.
      </p>

      <h2>Supported carriers</h2>
      <p>
        The WeConsult Rx SMS and RCS program is operated via Twilio, Inc., a
        CTIA- and carrier-approved A2P 10DLC messaging provider. Supported
        carriers include AT&amp;T, Verizon, T-Mobile, US Cellular, Sprint,
        Boost, MetroPCS, Cricket, and most US MVNOs.{" "}
        <strong>
          Carriers are not liable for any delayed or undelivered messages.
        </strong>
      </p>

      <h2>Mobile information sharing — explicit non-disclosure</h2>
      <p>
        <strong>
          Mobile information and messaging consent, including phone numbers and
          SMS/RCS opt-in status, are not shared with third parties or
          affiliates for marketing or promotional purposes.
        </strong>{" "}
        Information sharing is limited to subprocessors — primarily Twilio for
        message delivery and Supabase for at-rest storage — strictly as
        required to operate this SMS and RCS program. See our{" "}
        <a href="/subprocessors">Subprocessors</a> page for the full list and
        our <a href="/privacy">Privacy Policy</a> for details.
      </p>

      <h2>Eligibility and age</h2>
      <p>
        You must be at least eighteen (18) years old, the authorized account
        holder for the mobile phone number you provide, and located in the
        United States to opt in. By opting in, you represent that you meet
        these conditions.
      </p>

      <h2>Protected Health Information (PHI)</h2>
      <p>
        WeConsult Rx SMS and RCS messages <strong>do not contain</strong>{" "}
        patient names, medical record numbers, dates of birth, diagnoses,
        medications, or other Protected Health Information (PHI) regulated under
        HIPAA. This applies to both the text body and any rich media (images,
        cards, or attachments) that RCS is capable of carrying — no PHI is ever
        included in either. Message templates are restricted to a strict
        variable whitelist that includes only facility-level, account-level,
        and resource-identifier (UUID) fields. See our{" "}
        <a href="/hipaa">HIPAA Compliance</a> page for the technical controls
        that enforce this gate at both prompt and rendering time.
      </p>

      <h2>Changes to these SMS &amp; RCS Terms</h2>
      <p>
        We may update these SMS Terms from time to time. We will post the
        updated terms on this page with a new “Effective” date. Material changes
        will be communicated via in-product notice or email at least thirty
        (30) days in advance where practicable.
      </p>

      <h2>Contact</h2>
      <p>
        WeConsult Rx
        <br />
        DRX Consulting Group, LLC
        <br />
        Email:{" "}
        <a href="mailto:support@weconsultrx.com">
          support@weconsultrx.com
        </a>
        <br />
        General privacy contact:{" "}
        <a href="mailto:privacy@weconsultrx.com">privacy@weconsultrx.com</a>
      </p>

      <h2>Related policies</h2>
      <ul>
        <li>
          <a href="/privacy">Privacy Policy</a>
        </li>
        <li>
          <a href="/terms">Terms of Service</a>
        </li>
        <li>
          <a href="/hipaa">HIPAA Compliance Statement</a>
        </li>
        <li>
          <a href="/subprocessors">Subprocessors</a>
        </li>
      </ul>
    </LegalPage>
  );
}
